import { Component, EventEmitter, HostBinding, HostListener, Input, Output } from '@angular/core';

/**
 * BasicCheckboxComponent
 *
 * Purpose
 * - PCC-styled replacement for the default/native checkbox control
 * - Provides all interaction states (unchecked, hover, checked, focus, disabled) and two sizes
 * - Accessible with keyboard (Space/Enter) and ARIA attributes (supports mixed state for indeterminate)
 * - Supports three visual types: empty (filled), ticked (checkmark), indeterminate (dash)
 *
 * Visual states
 * - unchecked: default state with gray border
 * - hover: light purple border (#DBACF5)
 * - checked: filled with primary purple (#623875) background
 * - focus (unchecked): blue border (#0671E0) with glow effect
 * - focus (checked): blue background (#0671E0) with glow effect
 * - disabled: grayed out appearance, no interaction
 *
 * Sizes
 * - large: 14px × 14px checkbox box (default)
 * - small: 12px × 12px checkbox box
 *
 * Types
 * - ticked: solid fill + white checkmark when checked (default)
 * - empty: solid fill when checked, no icon
 * - indeterminate: solid fill + white horizontal dash when checked (aria-checked='mixed')
 *
 * Features
 * - Two-way data binding support
 * - Keyboard accessible (Space/Enter to toggle)
 * - ARIA attributes for accessibility
 * - Multiple visual types for different use cases
 *
 * Inputs
 * - label: string (optional) - Text displayed to the right of the checkbox
 * - size: 'large' | 'small' (default: 'large')
 * - disabled: boolean (default: false) - Disables interaction and applies disabled styling
 * - model: boolean (default: false) - Two-way bindable checked state
 * - type: 'empty' | 'ticked' | 'indeterminate' (default: 'ticked')
 *
 * Outputs
 * - change: boolean - Emits the new model value when user toggles the checkbox
 * - modelChange: boolean - Supports [(model)] two-way binding syntax
 *
 * Usage examples
 *
 * <!-- Basic large checkbox with label (default: ticked type) -->
 * <app-basic-checkbox label="Accept terms" [(model)]="acceptedTerms"></app-basic-checkbox>
 *
 * <!-- Two-way binding -->
 * <app-basic-checkbox [(model)]="isChecked" label="Subscribe to newsletter"></app-basic-checkbox>
 *
 * <!-- Small size -->
 * <app-basic-checkbox size="small" label="Small checkbox"></app-basic-checkbox>
 *
 * <!-- Empty type (filled, no icon) -->
 * <app-basic-checkbox type="empty" [(model)]="value" label="Empty type"></app-basic-checkbox>
 *
 * <!-- Indeterminate type (shows dash when checked) -->
 * <app-basic-checkbox type="indeterminate" [(model)]="value" label="Partially selected"></app-basic-checkbox>
 *
 * <!-- Disabled state -->
 * <app-basic-checkbox [model]="true" [disabled]="true" label="Disabled checked"></app-basic-checkbox>
 *
 * <!-- Controlled state with change handler -->
 * <app-basic-checkbox
 *   [model]="isOn"
 *   [disabled]="isDisabled"
 *   type="ticked"
 *   size="large"
 *   label="Controlled checkbox"
 *   (change)="onChanged($event)"
 *   (modelChange)="isOn = $event">
 * </app-basic-checkbox>
 */

type CheckboxSize = 'large' | 'small';
type CheckboxType = 'empty' | 'ticked' | 'indeterminate';

@Component({
    selector: 'app-basic-checkbox',
    templateUrl: './basic-checkbox.component.html',
    styleUrls: ['./basic-checkbox.component.scss'],
    standalone: false
})
export class BasicCheckboxComponent {
  @Input() label: string = '';
  @Input() size: CheckboxSize = 'large';
  @Input() disabled: boolean = false;
  @Input() model: boolean = false;
  @Input() type: CheckboxType = 'ticked';

  @Output() change = new EventEmitter<boolean>();
  @Output() modelChange = new EventEmitter<boolean>();

  @HostBinding('attr.role') role = 'group';
  @HostBinding('class.disabled') get isDisabledHost(): boolean { return this.disabled; }

  toggle(): void {
    if (this.disabled) { return; }
    // turning on/off clears indeterminate
    this.model = !this.model;
    this.change.emit(this.model);
    this.modelChange.emit(this.model);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const key = event.key.toLowerCase();
    if (key === ' ' || key === 'enter') {
      event.preventDefault();
      this.toggle();
    }
  }
}


